import { ChevronRightIcon } from '@forwardflow/icons'
import { OnboardingV2Service } from '@forwardflow/services/onboardingV2.service'
import { useAppStore } from '@forwardflow/store/appStore'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { Button, ContentCard, Stack } from '@forwardflow/ui-v2'
import { Grid, NumberInput, Slider } from '@mantine/core'
import { useState } from 'react'
import { usePricing } from '@forwardflow/modules/onboarding/hooks'
import { PricingSummary } from '@forwardflow/modules/onboarding/components'

const CostCard = ({ label, price }: { label: string; price: string }) => {
   return (
      <div
         className="rounded-xl p-4 px-8 border border-[#EB69C2]/[0.5] text-right"
         style={{
            background:
               'linear-gradient(111deg, rgba(159, 123, 255, 0.10) 1.32%, rgba(235, 105, 194, 0.10) 96.08%)',
         }}
      >
         <p className="text-white/50 text-sm font-medium">{label}</p>
         <p className="text-white font-semibold text-lg">{price}</p>
      </div>
   )
}

const SliderCard = ({
   min,
   max,
   defaultValue,
   value,
   onChange,
   label,
}: {
   min: number
   max: number
   defaultValue: number
   value: number
   onChange: (value: number) => void
   label?: (value: number) => string
}) => {
   return (
      <Slider
         min={min}
         max={max}
         defaultValue={defaultValue}
         value={value}
         classNames={{
            track: '!bg-[#D9D9D9]/10 !h-2 rounded-full',
            bar: '!bg-gradient-to-r !from-blue-500 !to-purple-500 !h-2',
            thumb: '!bg-white !w-4 !h-4 !rounded-full !border-0',
         }}
         onChange={onChange}
         label={label}
      />
   )
}

export const PricingStepV2 = () => {
   const { onboardingData, getNextStep, setOrganizationId } =
      useOnboardingStoreV2()
   const { setCurrentOrganization } = useAppStore()
   const { email, isSubscriptionActivated } = onboardingData
   const [isOnboardingStarting, setIsOnboardingStarting] = useState(false)

   // Use pricing hook for all calculations and handlers
   const {
      EAC_NEW_LEAD,
      EAC_COLD_LEAD,
      seatsCount,
      newLeadCount,
      reactivateLeadCount,
      seatCost,
      newLeadsCost,
      coldLeadsCost,
      handlePricingDataChange,
   } = usePricing()

   // *** handlers ***
   const handleStartOnboarding = async () => {
      // if (
      //    isOperatorEmail(email || '') &&
      //    ONBOARDING_DUMMY_ORGANIZATION_ID
      // ) {
      //    getNextStep()
      //    return
      // }
      try {
         if (!email) {
            return
         }
         setIsOnboardingStarting(true)
         const data = await OnboardingV2Service.startOnboarding({
            email,
         })
         const organizationId = data?.data?.id
         if (data?.success && organizationId) {
            setCurrentOrganization(organizationId)
            setOrganizationId(organizationId)
            getNextStep()
         }
      } catch (error) {
         console.error(error)
      } finally {
         setIsOnboardingStarting(false)
      }
   }

   return (
      <Stack gap={32}>
         <ContentCard className="space-y-2">
            <p className="text-white font-medium">
               Welcome to Forward Flow! Pick the perfect plan for your team
            </p>
            <p className="text-white/70 text-sm font-medium">
               Choose a billing cycle and a plan that fits your team. You can
               switch anytime from your account settings.
            </p>
         </ContentCard>
         <ContentCard className="!space-y-4">
            <Stack gap={4}>
               <p className="text-white font-medium">How big is your team?</p>
               <p className="text-[#99A1AF] text-sm">
                  Adjust the slider to set the number Forward Flow users
               </p>
            </Stack>
            <Grid className="items-center">
               <Grid.Col span={8} className="flex items-center">
                  <div className="bg-white/[0.02] rounded-lg p-4 w-full">
                     <Grid>
                        <Grid.Col
                           span={4}
                           className="text-white text-md font-medium whitespace-nowrap"
                        >
                           <NumberInput
                              min={1}
                              max={50}
                              value={seatsCount}
                              classNames={{
                                 input: 'border border-[#B17AFF]/[0.5] rounded-lg p-2',
                                 section: 'w-20',
                              }}
                              onChange={(value) =>
                                 handlePricingDataChange(
                                    'seatsCount',
                                    Number(value)
                                 )
                              }
                              rightSection={
                                 <span className="text-white/60 text-xs">
                                    Users
                                 </span>
                              }
                           />
                        </Grid.Col>
                        <Grid.Col
                           span={8}
                           className="flex flex-col justify-center w-full"
                        >
                           <SliderCard
                              min={1}
                              max={50}
                              defaultValue={1}
                              value={seatsCount}
                              onChange={(value) =>
                                 handlePricingDataChange('seatsCount', value)
                              }
                           />
                        </Grid.Col>
                     </Grid>
                  </div>
               </Grid.Col>
               <Grid.Col span={4}>
                  <CostCard label="Seat Cost" price={`$${seatCost}/month`} />
               </Grid.Col>
            </Grid>
         </ContentCard>

         <ContentCard className="!space-y-4">
            <Stack gap={4}>
               <p className="text-white font-medium">
                  How many new leads do you want us to engage every month?
               </p>
               <p className="text-[#99A1AF] text-sm">
                  New lead costs up to ${EAC_NEW_LEAD}/month.
               </p>
            </Stack>
            <Grid className="items-center">
               <Grid.Col span={8} className="flex items-center">
                  <div className="bg-white/[0.02] rounded-lg p-4 w-full">
                     <Grid>
                        <Grid.Col
                           span={4}
                           className="text-white text-md font-medium whitespace-nowrap"
                        >
                           <NumberInput
                              min={1}
                              max={2000}
                              value={newLeadCount}
                              classNames={{
                                 input: 'border border-[#B17AFF]/[0.5] rounded-lg p-2',
                                 section: 'w-20',
                              }}
                              onChange={(value) =>
                                 handlePricingDataChange(
                                    'newLeadCount',
                                    Number(value)
                                 )
                              }
                              rightSection={
                                 <span className="text-white/60 text-xs">
                                    New leads
                                 </span>
                              }
                           />
                        </Grid.Col>
                        <Grid.Col
                           span={8}
                           className="flex flex-col justify-center w-full"
                        >
                           <SliderCard
                              min={1}
                              max={2000}
                              defaultValue={1}
                              value={newLeadCount}
                              onChange={(value) =>
                                 handlePricingDataChange('newLeadCount', value)
                              }
                           />
                        </Grid.Col>
                     </Grid>
                  </div>
               </Grid.Col>
               <Grid.Col span={4}>
                  <CostCard
                     label="Nurture Cost"
                     price={`$${newLeadsCost}/Month`}
                  />
               </Grid.Col>
            </Grid>
         </ContentCard>

         <ContentCard className="!space-y-4">
            <Stack gap={4}>
               <p className="text-white font-medium">
                  How many cold leads do you want us to revive every month?
               </p>
               <p className="text-[#99A1AF] text-sm">
                  Revive lead costs up to ${EAC_COLD_LEAD}/month.
               </p>
            </Stack>
            <Grid className="items-center">
               <Grid.Col span={8} className="flex items-center">
                  <div className="bg-white/[0.02] rounded-lg p-4 w-full">
                     <Grid>
                        <Grid.Col
                           span={4}
                           className="text-white text-md font-medium whitespace-nowrap"
                        >
                           <NumberInput
                              min={1}
                              max={10000}
                              value={reactivateLeadCount}
                              onChange={(value) =>
                                 handlePricingDataChange(
                                    'reactivateLeadCount',
                                    Number(value)
                                 )
                              }
                              classNames={{
                                 input: 'border border-[#B17AFF]/[0.5] rounded-lg p-2',
                                 section: 'w-20',
                              }}
                              rightSection={
                                 <span className="text-white/60 text-xs">
                                    Revive leads
                                 </span>
                              }
                           />
                        </Grid.Col>
                        <Grid.Col
                           span={8}
                           className="flex flex-col justify-center w-full"
                        >
                           <SliderCard
                              min={1}
                              max={10000}
                              defaultValue={1}
                              value={reactivateLeadCount}
                              onChange={(value) =>
                                 handlePricingDataChange(
                                    'reactivateLeadCount',
                                    value
                                 )
                              }
                           />
                        </Grid.Col>
                     </Grid>
                  </div>
               </Grid.Col>
               <Grid.Col span={4}>
                  <CostCard
                     label="Revival Cost"
                     price={`$${coldLeadsCost}/Month`}
                  />
               </Grid.Col>
            </Grid>
         </ContentCard>
         <div className="bg-[#A684FF1A] rounded-[16px] p-4 border border-[#A684FF]/[0.10] space-y-4">
            <PricingSummary />
            <p className="text-white/70 text-sm border-b border-white/10 pb-4">
               Any unused nurture credits (new or revive leads) carry over to
               the next month.
               <br />
               Next month, we only bill you for the difference needed to reach
               your selected monthly budget.
            </p>
            <Button
               className="w-full"
               onClick={handleStartOnboarding}
               loading={isOnboardingStarting}
            >
               {isSubscriptionActivated
                  ? 'Continue'
                  : 'Confirm Plan and Continue'}{' '}
               <ChevronRightIcon width={18} />
            </Button>
         </div>
      </Stack>
   )
}
